import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Material } from "#modules/materials/infra/fetchCustomMaterials.api";
import { useCreateMaterial } from "#modules/materials/view/useCreateMaterial";
import { useUpdateMaterial } from "#modules/materials/view/useUpdateMaterial";
import { getUnitFactorsByMaterialType } from "#modules/quoteRequestLines/domain/getUnitFactors";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { NativeSelectInputController } from "#shared/view/designSystem/inputs/NativeSelectInputController";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { RecommendedPriceInputController } from "#shared/view/designSystem/inputs/RecommendedPriceInputController";
import { TextInputController } from "#shared/view/designSystem/inputs/TextInputController";
import {
  assertUnreachable,
  getMaterialDensity,
  MaterialGradeSchema,
  MaterialGradeType,
  MaterialTypeSchema,
  MaterialTypeType,
  PartTypeType,
} from "shared";

type FormData = {
  name: string;
  type: MaterialTypeType;
  subType: string;
  materialGrade: MaterialGradeType;
  length: number;
  width: number;
  height: number;
  tubeThickness: number | null;
  density: number | null;
  price: number;
};

type CommonProps = {
  opened: boolean;
  onClose: () => void;
};

type CreateProps = CommonProps & {
  mode: "create";
  initialData?: undefined;
};

type EditProps = CommonProps & {
  mode: "edit";
  initialData: Material;
};

type Props = CreateProps | EditProps;

const getDefaultValues = (initialData?: Material): FormData => {
  const materialGrade = initialData?.materialGrade ?? MaterialGradeSchema.enum.STEEL;

  return {
    name: initialData?.name ?? "",
    type: initialData?.type ?? MaterialTypeSchema.enum.SHEET,
    subType: initialData?.subType ?? "",
    materialGrade,
    length: initialData?.length ?? 0,
    width: initialData?.width ?? 0,
    height: initialData?.height ?? 0,
    tubeThickness: initialData?.tubeThickness ?? null,
    density: initialData?.density ?? getMaterialDensity({ materialGrade }),
    price: initialData?.price ?? 0,
  };
};

export const MaterialFormModal = ({ opened, onClose, mode, initialData }: Props) => {
  const { t } = useTranslation();
  const { createMaterial, isPending: isCreating } = useCreateMaterial();
  const { updateMaterial, isPending: isUpdating } = useUpdateMaterial();

  const methods = useForm<FormData>({
    defaultValues: getDefaultValues(initialData),
  });

  const { control, handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    if (opened) {
      reset(getDefaultValues(initialData));
    }
  }, [opened, initialData, reset]);

  const handleClose = () => {
    reset(getDefaultValues());
    onClose();
  };

  const materialTypeOptions = useMemo(
    () =>
      Object.values(MaterialTypeSchema.enum).map((type) => ({
        value: type,
        label: t(`qrl.materialTypes.${type}`),
      })),
    [t],
  );

  const materialGradeOptions = useMemo(
    () =>
      Object.values(MaterialGradeSchema.enum)
        .map((grade) => ({
          value: grade,
          label: t(`qrl.materialGrades.${grade}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [t],
  );

  const [type, length, width, height, tubeThickness, density] = useWatch({
    control,
    name: ["type", "length", "width", "height", "tubeThickness", "density"],
  });

  const priceUnitFactors = useMemo(() => {
    const factors = getUnitFactorsByMaterialType({
      partType: type as PartTypeType,
      material: {
        densityKgDm3: density ?? 0,
        length,
        width,
        height,
        tubeThickness: tubeThickness ?? 0,
      },
    });

    return factors && factors.length > 0 ? factors : undefined;
  }, [type, length, width, height, tubeThickness, density]);

  const onSubmit = (data: FormData) => {
    const payload = {
      name: data.name,
      type: data.type,
      subType: data.subType || null,
      materialGrade: data.materialGrade,
      length: data.length,
      width: data.width,
      height: data.height,
      tubeThickness: data.tubeThickness,
      density: data.density,
      price: Math.round(data.price),
    };

    switch (mode) {
      case "create":
        createMaterial(payload, { onSuccess: handleClose });
        break;
      case "edit":
        updateMaterial({ id: initialData.id, payload }, { onSuccess: handleClose });
        break;
      default:
        assertUnreachable(mode);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        mode === "create"
          ? t("settings.materials.createModal.title")
          : t("settings.materials.editModal.title")
      }
      size="md"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            void handleSubmit(onSubmit)(e);
          }}
        >
          <Stack gap="md">
            <TextInputController
              name="name"
              label={t("settings.materials.name")}
              control={control}
              required
            />

            <Flex gap="xs">
              <NativeSelectInputController
                name="type"
                label={t("settings.materials.type")}
                data={materialTypeOptions}
                withEmptyData={false}
                required
              />
            </Flex>

            <Flex gap="xs">
              <NativeSelectInputController
                name="materialGrade"
                label={t("qrl.materialGrade")}
                data={materialGradeOptions}
                withEmptyData={false}
                required
                onChange={(value) =>
                  setValue(
                    "density",
                    getMaterialDensity({ materialGrade: value as MaterialGradeType }),
                  )
                }
              />
            </Flex>

            <Flex gap="xs">
              <NumberInputController
                name="length"
                label={t("settings.materials.length")}
                control={control}
                min={0}
                required
              />
              <NumberInputController
                name="width"
                label={t("settings.materials.width")}
                control={control}
                min={0}
                required
              />
              <NumberInputController
                name="height"
                label={t("settings.materials.height")}
                control={control}
                min={0}
                required
              />
            </Flex>

            <Flex gap="xs">
              <NumberInputController
                name="tubeThickness"
                label={t("settings.materials.tubeThickness")}
                control={control}
                min={0}
              />
              <NumberInputController
                name="density"
                required
                label={t("settings.materials.density")}
                control={control}
                min={0}
              />
            </Flex>

            <RecommendedPriceInputController
              name="price"
              label={t("settings.materials.price")}
              unit="€"
              unitFactors={priceUnitFactors}
              control={control}
              required
            />

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={isPending}>
                {mode === "create" ? t("common.add") : t("common.validate")}
              </Button>
            </Group>
          </Stack>
        </form>
      </FormProvider>
    </Modal>
  );
};
